/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Theme, useMediaQuery, useTheme } from "@mui/material";
import MuiTab from "@mui/material/Tab"; // Import TabProps
import MuiTabs from "@mui/material/Tabs";
import { styled } from "@mui/system";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import urlcat from "urlcat";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      width="100%"
      paddingY={4}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

const Tabs = styled(MuiTabs)(({ theme }) => ({
  "& .MuiTabs-flexContainer": {
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: "hidden",
    height: "100%",
  },
}));

const Tab = styled(MuiTab)(({ theme }) => ({
  writingMode: "vertical-rl",
  rotate: "180deg",
  textTransform: "none",
  padding: "44px 20px",
  color: theme.palette.text.primary,
  border: 0,
  fontWeight: 500,
  flex: 1,
  "&.Mui-selected": {
    fontWeight: 500,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const HorizontalTabs = styled(MuiTabs)(({ theme }) => ({
  "& .MuiTabs-flexContainer": {
    overflow: "hidden",
    height: "100%",
    ".MuiTab-root": {
      "&:first-of-type": {
        borderTopLeftRadius: 12,
      },
      "&:last-of-type": {
        borderTopRightRadius: 12,
      },
    },
  },
}));

const HorizontalTab = styled(MuiTab)(({ theme }) => ({
  textTransform: "none",
  color: theme.palette.text.primary,
  border: 0,
  fontWeight: 500,
  flex: 1,
  "&.Mui-selected": {
    fontWeight: 500,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

interface VerticalTabsProps {
  items: React.ReactNode[];
}

export default function VerticalTabs({ items }: VerticalTabsProps) {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { return_visitor, ...params } = Object.fromEntries(
    queryParams.entries()
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [value, setValue] = useState(
    return_visitor === "false" || return_visitor === undefined ? 0 : 1
  );

  const handleChange = useCallback(
    (_event: React.SyntheticEvent | undefined, newValue: number) => {
      setValue(newValue);
      navigate(urlcat(location.pathname, { ...params }));
    },
    [location.pathname, params, navigate]
  );

  useEffect(() => {
    if (return_visitor === "false" || return_visitor === undefined) {
      handleChange(undefined, 0);
    }
  }, [return_visitor]);

  if (isMobile) {
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          minHeight: 410,
        }}
      >
        <HorizontalTabs
          value={value}
          scrollButtons={false}
          onChange={handleChange}
          aria-label="Vertical tabs example"
          variant="fullWidth"
          sx={{ border: 1, borderColor: "divider" }}
        >
          <HorizontalTab label="New Visitor" {...a11yProps(0)} />
          <HorizontalTab label="Return Visitor" {...a11yProps(1)} />
        </HorizontalTabs>
        <TabPanel value={value} index={0}>
          {items[0]}
        </TabPanel>
        <TabPanel value={value} index={1}>
          {items[1]}
        </TabPanel>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        bgcolor: "background.paper",
        display: "flex",
        minHeight: 410,
      }}
    >
      <Tabs
        value={value}
        scrollButtons={false}
        orientation="vertical"
        onChange={handleChange}
        aria-label="Vertical tabs example"
        sx={{ border: 1, borderColor: "divider" }}
      >
        <Tab label="New Visitor" {...a11yProps(0)} />
        <Tab label="Return Visitor" {...a11yProps(1)} />
      </Tabs>
      <TabPanel value={value} index={0}>
        {items[0]}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {items[1]}
      </TabPanel>
    </Box>
  );
}
